import React from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Chip, 
  Grid,
  Avatar,
  Divider,
  Stack,
  useTheme
} from '@mui/material';
import { 
  DirectionsCar as CarIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Speed as SpeedIcon,
  SquareFoot as DimensionsIcon,
  Scale as WeightIcon
} from '@mui/icons-material';
import { Driver } from '../../models/userInfo';

interface DriverCardProps {
  driver: Driver;
  isSelected: boolean;
  onClick: () => void;
}

/**
 * Component to display a driver's information in a card format
 */
const DriverCard: React.FC<DriverCardProps> = ({ driver, isSelected, onClick }) => {
  const theme = useTheme();
  const { name, phNo, vendorName, vehicle } = driver;
  
  // Helper function to format dimensions
  const formatDimensions = (lbh: { lcm: number; bcm: number; hcm: number }) => {
    return `${lbh.lcm} × ${lbh.bcm} × ${lbh.hcm} cm`;
  };
  
  return (
    <Card 
      onClick={onClick}
      sx={{
        mb: 2,
        cursor: 'pointer',
        border: isSelected ? `2px solid ${theme.palette.primary.main}` : 'none',
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: 3,
          transform: 'translateY(-2px)'
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar 
            sx={{ 
              bgcolor: isSelected ? theme.palette.primary.main : theme.palette.secondary.light,
              mr: 2 
            }}
          >
            <PersonIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" component="div">
              {name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
              <PhoneIcon fontSize="small" sx={{ mr: 0.5 }} />
              {phNo}
            </Typography>
          </Box>
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {vendorName}
          </Typography>
        </Box>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <CarIcon fontSize="small" sx={{ mr: 1 }} />
            Vehicle Details
          </Typography>
          
          <Grid container spacing={1} sx={{ pl: 3 }}>
            <Grid item xs={12}>
              <Typography variant="body2">
                <strong>Reg:</strong> {vehicle.regNumber}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                <WeightIcon fontSize="small" sx={{ mr: 0.5 }} />
                {vehicle.wtCapKg} kg
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                <SpeedIcon fontSize="small" sx={{ mr: 0.5 }} />
                {vehicle.maxRangeKm} km
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                <DimensionsIcon fontSize="small" sx={{ mr: 0.5 }} />
                {formatDimensions(vehicle.lbh)}
              </Typography>
            </Grid>
          </Grid>
        </Box>
        
        <Stack direction="row" spacing={1}>
          <Chip 
            label={vehicle.vehicleType} 
            size="small" 
            color={isSelected ? "primary" : "default"}
          />
          <Chip 
            label={vehicle.contractType} 
            size="small" 
            variant="outlined"
          />
          <Chip 
            label={`Max TP: ${vehicle.maxTp}`} 
            size="small" 
            variant="outlined"
          />
        </Stack>
      </CardContent>
    </Card>
  );
};

export default DriverCard; 